/**
 * «منشی هوشمند» in the doctor panel — the record button (PM-0517). Press: record.
 * Press again: stop, and the command is transcribed and run in the BACKGROUND while
 * the doctor carries on. What comes back waits in `items` — shown as notifications —
 * until it is opened or dismissed: a result may already have cost پارسه, so it
 * never leaves on a timer.
 *
 * ── WHAT IS NEVER SPENT ──────────────────────────────────────────────────────
 * A balance known to be too small never starts the microphone. A recording thrown
 * away (× or Esc) is cancelled, never transcribed. Nothing heard is no command: no
 * result, no notification — the button says so. And a command is one sentence: a
 * recording left running stops itself after RECORD_LIMIT_S, and is sent.
 *
 * ONE microphone: the speech stream is the kit's server dictation (the visit's
 * /ws/transcribe with no visit and hints off), and the next command can be recorded
 * as soon as the last one's words are in — while it still runs.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { bffMutation } from '@parsina/framework/clinical/src/lib/bffClient';
import { useDoctor } from '@parsina/framework/clinical/src/hooks/useDoctor';
import { useServerDictation } from '@parsina/framework/clinical/src/hooks/useServerDictation';
import { notifyWalletChanged, useWalletBalance } from '@parsina/framework/clinical/src/hooks/useWalletBalance';
import { costOf, loadCreditPrices } from '@parsina/framework/clinical/src/lib/useCreditPrices';
import { ALL_CLINICS, readActiveClinicId } from '@parsina/framework/clinical/src/lib/activeClinic';

import { PRICE_SERVICE, PRICE_TRANSCRIPTION, RUN_SECRETARY_COMMAND, cannotAfford, isCreditRefusal } from './model';

export const RECORD_LIMIT_S = 60;
/** How long the button says «چیزی نشنیدم» (or why the microphone would not open). */
export const WARN_MS = 3500;

const MIC_FAILURES = new Set(['denied', 'unsupported', 'error']);

let sequence = 0;
const nextId = () => {
  sequence += 1;
  return `cmd-${Date.now().toString(36)}-${sequence}`;
};

/** پذیرش's clinic, if the doctor picked one — a default the BFF checks, never a choice. */
function receptionClinicId() {
  const id = readActiveClinicId();
  return id && id !== ALL_CLINICS ? id : null;
}

/** The planning step's price from the policy list, or null — then the server decides. */
async function commandPrice() {
  const state = await loadCreditPrices();
  const price = state?.byService?.get?.(PRICE_SERVICE);
  return Number.isFinite(price) ? price : null;
}

export function useSecretaryController() {
  const voice = useServerDictation({ engine: 'parsina' });
  const { doctor } = useDoctor();
  const balance = useWalletBalance(doctor?.id);
  const voiceRef = useRef(voice);
  voiceRef.current = voice;
  const balanceRef = useRef(balance);
  balanceRef.current = balance;

  const [recording, setRecordingState] = useState(false);
  const recordingRef = useRef(false);
  const setRecording = useCallback((on) => { recordingRef.current = on; setRecordingState(on); }, []);
  // Between «stop» and the words being in: the microphone is not free yet.
  const transcribing = useRef(false);
  const [seconds, setSeconds] = useState(0);
  // Commands between «stop» and their result.
  const [pending, setPending] = useState(0);
  const [warn, setWarn] = useState(null);
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const flash = useCallback((kind, detail = null) => setWarn({ kind, detail, at: Date.now() }), []);
  useEffect(() => {
    if (!warn) return undefined;
    const id = setTimeout(() => setWarn(null), WARN_MS);
    return () => clearTimeout(id);
  }, [warn]);

  const live = recording && voice.status === 'listening';
  useEffect(() => {
    if (!live) return undefined;
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [live]);

  const addItem = useCallback((item) => setItems((list) => [...list, item]), []);
  const update = useCallback((id, patch) => {
    setItems((list) => list.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  }, []);
  const dismiss = useCallback((id) => {
    setItems((list) => list.filter((item) => item.id !== id));
    setOpenId((current) => (current === id ? null : current));
  }, []);

  /** Run one command — a new one, or the same words with the doctor's choice of patient. */
  const run = useCallback(async (id, text, choice = {}) => {
    setPending((n) => n + 1);
    update(id, { status: 'processing' });
    try {
      const data = await bffMutation(RUN_SECRETARY_COMMAND, {
        input: {
          transcript: text,
          patientId: choice.patientId || null,
          newPatient: choice.newPatient === true,
          receptionClinicId: receptionClinicId(),
        },
      });
      if (!alive.current) return;
      update(id, { status: 'ready', result: data?.runSecretaryCommand || { outcome: 'UNKNOWN', charged: false }, error: null });
    } catch (err) {
      if (!alive.current) return;
      update(id, { status: 'ready', error: isCreditRefusal(err) ? 'credit' : 'failed' });
    } finally {
      if (alive.current) setPending((n) => n - 1);
      // The sidebar's balance and the next «can I afford it» both read the wallet.
      notifyWalletChanged();
    }
  }, [update]);

  const start = useCallback(async () => {
    if (recordingRef.current) return;
    if (transcribing.current) { flash('busy'); return; }
    setRecording(true);
    const command = await commandPrice();
    if (!alive.current || !recordingRef.current) return;
    if (cannotAfford(balanceRef.current, costOf(PRICE_TRANSCRIPTION), command)) {
      setRecording(false);
      addItem({ id: nextId(), createdAt: Date.now(), status: 'ready', text: '', result: null, error: 'credit' });
      return;
    }
    setWarn(null);
    setSeconds(0);
    // Each command is its own sentence: nothing of the last one is carried over.
    voiceRef.current.reset();
    voiceRef.current.start();
  }, [addItem, flash, setRecording]);

  const stop = useCallback(async () => {
    if (!recordingRef.current) return;
    const v = voiceRef.current;
    setRecording(false);
    if (v.status !== 'listening') {
      // Pressed again before the stream was up: nothing was recorded, nothing is sent.
      v.cancel();
      return;
    }
    transcribing.current = true;
    setPending((n) => n + 1);
    const text = String((await v.finish()) || '').trim();
    transcribing.current = false;
    if (!alive.current) return;
    setPending((n) => n - 1);
    if (!text) { flash('nothing'); return; }
    const id = nextId();
    addItem({ id, createdAt: Date.now(), status: 'processing', text, result: null, error: null });
    run(id, text);
  }, [addItem, flash, run, setRecording]);

  /** × or Esc — throw the recording away: closed without a final pass, never transcribed. */
  const discard = useCallback(() => {
    if (!recordingRef.current) return;
    voiceRef.current.cancel();
    setRecording(false);
  }, [setRecording]);

  const toggle = useCallback(() => {
    if (recordingRef.current) stop();
    else start();
  }, [start, stop]);

  // The microphone refused: say why on the button, and record nothing.
  useEffect(() => {
    if (!recording || !MIC_FAILURES.has(voice.status)) return;
    setRecording(false);
    flash('mic', voice.status);
  }, [recording, voice.status, flash, setRecording]);

  // A command is one sentence: a recording left running stops itself, and is sent.
  useEffect(() => {
    if (recording && seconds >= RECORD_LIMIT_S) stop();
  }, [recording, seconds, stop]);

  /** The doctor answered «کدام‌یک؟»: the same words again, with their choice. */
  const answer = useCallback((id, choice) => {
    const item = items.find((i) => i.id === id);
    if (!item?.text) return;
    run(id, item.text, choice);
  }, [items, run]);

  const ready = useMemo(() => items.filter((item) => item.status === 'ready' && item.id !== openId), [items, openId]);
  const openItem = useMemo(() => items.find((item) => item.id === openId) || null, [items, openId]);

  return {
    recording,
    live,
    seconds,
    remaining: Math.max(0, RECORD_LIMIT_S - seconds),
    pending,
    warn,
    items: ready,
    openItem,
    start,
    stop,
    discard,
    toggle,
    answer,
    open: setOpenId,
    close: useCallback(() => setOpenId(null), []),
    dismiss,
  };
}

export default useSecretaryController;
